import { useMutation } from '@apollo/client';
import {
  Arrange,
  Container,
  Switch,
  Text,
  Toast,
  Icon,
  Spacer,
  SvgChevronDown,
} from '@loomhq/lens';
import React, { useMemo, useState } from 'react';
import { useLayer } from 'react-laag';
import { useSelector } from 'react-redux';

import { VideoVisibility as VisibilityType } from 'constants/visibility';

import useClickOutside from 'hooks/useClickOutside';
import { selectWorkspaceId } from 'selectors/workspace';

import UpdateVideoVisibility from 'creators/UpdateVideoVisibility.graphql';

import styles from './styles.module.less';

type VideoVisibilityProps = {
  videoId: string;
  videoVisibility: VisibilityType;
  textSize?: string;
  // onVisibilityUpdate is a callback to execute once the video visibility has updated
  onVisibilityUpdate?: (visibility: string) => void;
};

export const VideoVisibility = ({
  videoVisibility,
  videoId,
  textSize = 'medium',
  onVisibilityUpdate,
}: VideoVisibilityProps) => {
  const [isVisibilityPopoverOpen, setIsVisibilityPopoverOpen] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen: isVisibilityPopoverOpen,
    ResizeObserver,
    placement: 'bottom-start',
    auto: true,
  });

  useMemo(() => {
    const isPublished = videoVisibility?.includes(
      VisibilityType.WORKSPACE,
      VisibilityType.PUBLIC
    );

    setIsPublished(isPublished);
  }, [videoVisibility]);

  const onVisibilityClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisibilityPopoverOpen(!isVisibilityPopoverOpen);
  };

  const publishedText = (
    <Text fontWeight="medium" color="primary" size={textSize}>
      Published
    </Text>
  );
  const unpublishedText = (
    <Text color="bodyDimmed" size={textSize}>
      Unpublished
    </Text>
  );

  return (
    <>
      <button
        className={styles.visibilityButton}
        {...triggerProps}
        onClick={onVisibilityClick}
      >
        {isPublished ? publishedText : unpublishedText}
        <Icon icon={<SvgChevronDown />} size="2" />
      </button>

      {isVisibilityPopoverOpen &&
        renderLayer(
          <span className={styles.visibilityCard} {...layerProps}>
            <VisibilityPopover
              videoId={videoId}
              isPublished={isPublished}
              setIsVisibilityPopoverOpen={setIsVisibilityPopoverOpen}
              onVisibilityUpdate={onVisibilityUpdate}
            />
          </span>
        )}
    </>
  );
};

type VisibilityPopoverProps = {
  videoId: string;
  isPublished: boolean;
  setIsVisibilityPopoverOpen: (isOpen: boolean) => void;
  onVisibilityUpdate: (visibility: string) => void;
};

const VisibilityPopover = ({
  videoId,
  isPublished,
  setIsVisibilityPopoverOpen,
  onVisibilityUpdate,
}: VisibilityPopoverProps) => {
  const [isUpdateVisibilityError, setIsUpdateVisibilityError] = useState(false);
  const ref = useClickOutside(() => setIsVisibilityPopoverOpen(false));
  const workspaceId = useSelector(selectWorkspaceId);

  const [updateVideoVisibility, { loading }] = useMutation(
    UpdateVideoVisibility,
    {
      onCompleted: data => {
        onVisibilityUpdate &&
          onVisibilityUpdate(data.updateVideoVisibility.video.visibility);
        setIsUpdateVisibilityError(false);
      },
      onError: () => {
        setIsUpdateVisibilityError(true);
      },
    }
  );

  const onVisibilitySwitchChange = () => {
    // Don't allow updating visibility if visibility
    // update is not complete (don't want someone spamming)
    if (loading) {
      return;
    }

    setIsUpdateVisibilityError(false);
    updateVideoVisibility({
      variables: {
        videoId,
        workspaceId,
        visibility: isPublished
          ? VisibilityType.OWNER
          : VisibilityType.WORKSPACE,
      },
    });
  };

  return (
    <div
      onClick={e => {
        e.stopPropagation();
      }}
      ref={ref}
    >
      <Container
        padding="medium"
        backgroundColor="background"
        borderSide="all"
        radius="medium"
        shadow="medium"
        width="17.5rem"
      >
        <Arrange justifyContent="space-between">
          <Text fontWeight="bold">Publish your video</Text>
          <Switch onChange={onVisibilitySwitchChange} isActive={isPublished} />
        </Arrange>
        <Spacer top="medium" />
        <Text variant="body" htmlTag="p" color="bodyDimmed">
          A published video will appear in feeds, search results, and your
          profile.
        </Text>
      </Container>

      <Toast
        isOpen={Boolean(isUpdateVisibilityError)}
        onCloseClick={() => setIsUpdateVisibilityError(false)}
        zIndex={5}
      >
        Failed to update video visibility.
      </Toast>
    </div>
  );
};
